import {useEffect} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import {useInitializeSurvicateScript} from '@hooks/Survicate/useInitializeSurvicateScript';
import {useCeremonyRoute} from '@hooks/useCeremonyRoute';
import {selectConfig, selectTheme, selectSurvicateWorkspaceKey} from '@components/appSlice';
import {DEFAULT_LOCALE} from '@constants/main';
import SignerUiHeader from '@components/common/SignerUiHeader/SignerUiHeader';
import SignerUiMenu from '@components/common/SignerUiHeader/SignerUiMenu/SignerUiMenu';
import ReassignPanel from '@components/common/Header/UserMenu/SubMenus/ReassignPanel/ReassignPanel';
import DeclinePanel from '@components/common/Header/UserMenu/SubMenus/DeclinePanel/DeclinePanel';
import Loading from '@components/shared/Loading/Loading';
import {
    selectIsLeftMenuOpen,
    selectRecipientLanguage,
    selectIsInitialLoadInProgress,
    selectActiveAccountId,
    selectAccount,
    selectDefaultBrand,
    selectRecipient,
    selectTransaction,
    selectDocuments,
    selectApprovals,
    selectFields,
    selectAttachments,
    selectConfigurations,
    selectDocumentVisibility,
    selectAllRecipients,
    selectVerification,
    selectAccountProvider,
    selectSignature,
    selectSignerUser,
    selectAccountFeatures,
    selectGroup,
    selectDelegateUser
} from '@components/SignerUi/signerUiSlice';
import {useSignerData} from '@components/SignerUi/hooks/useSignerData';
import {useFaceToFaceRole} from '@components/SignerUi/hooks/useFaceToFaceRole';
import {selectAccessToken} from '@components/Login/userSlice';
import {parseJWT} from '@utils/jwtUtils';
import SignerUiIntlProvider from '@components/SignerUi/SignerUiIntlProvider';
import {HardwareTokenSigningContainer} from '@components/SignerUi/HardwareTokenSigning';
import useIframeViewportHeight from '@hooks/useIframeViewportHeight';
import useKeyboardVisibility from '@hooks/useKeyboardVisibility';
import IFrameEventNotifications from '@utils/iFrameNotifyer/IFrameEventNotifications';
import {getCeremonyViewportStyle} from './utils';
import {useTwoFARedirect} from './useTwoFARedirect';
import DeclinedInterceptor from './DeclinedInterceptor';
import {useFutureSignerRedirectPath} from './useFutureSignerRedirectPath';
import './signingCeremony.less';

export default function SigningCeremony() {
    useEffect(() => {
        console.log('[SigningCeremony] MOUNTED');
        IFrameEventNotifications.getInstance().setListener();
        return () => console.log('[SigningCeremony] UNMOUNTED');
    }, []);

    const ceremonyRoute = useCeremonyRoute();
    const hasLeftMenu = ceremonyRoute?.isLeftMenuVisible !== false;

    const isLeftMenuOpen = useSelector(selectIsLeftMenuOpen);
    const isInitialLoadInProgress = useSelector(selectIsInitialLoadInProgress);
    const locale = useSelector(selectRecipientLanguage);
    const survicateWorkspaceKey = useSelector(selectSurvicateWorkspaceKey);
    const theme = useSelector(selectTheme);
    const config = useSelector(selectConfig);

    const activeAccountId = useSelector(selectActiveAccountId);
    const account = useSelector(selectAccount);
    const defaultBrand = useSelector(selectDefaultBrand);
    const recipient = useSelector(selectRecipient);
    const transaction = useSelector(selectTransaction);
    const documents = useSelector(selectDocuments);
    const approvals = useSelector(selectApprovals);
    const fields = useSelector(selectFields);
    const attachments = useSelector(selectAttachments);
    const configurations = useSelector(selectConfigurations);
    const documentVisibility = useSelector(selectDocumentVisibility);
    const allRecipients = useSelector(selectAllRecipients);
    const verification = useSelector(selectVerification);
    const accountProvider = useSelector(selectAccountProvider);
    const signature = useSelector(selectSignature);
    const signerUser = useSelector(selectSignerUser);
    const accountFeatures = useSelector(selectAccountFeatures);
    const group = useSelector(selectGroup);
    const delegateUser = useSelector(selectDelegateUser);
    const accessToken = useSelector(selectAccessToken);
    const parsedAccessToken = accessToken ? parseJWT(accessToken) : null;

    console.log('[SigningCeremony] parsedAccessToken:', parsedAccessToken);
    console.log('[SigningCeremony] parsedAccessToken claims:', {
        role: parsedAccessToken?.role,
        external_id: parsedAccessToken?.external_id,
        exchange_reason: parsedAccessToken?.exchange_reason,
        exchange_actor_user_id: parsedAccessToken?.exchange_actor_user_id,
        azp: parsedAccessToken?.azp,
        transaction_id: parsedAccessToken?.transaction_id,
        email_aliases: parsedAccessToken?.email_aliases
    });
    console.log('[SigningCeremony] user:', signerUser);
    console.log('[SigningCeremony] delegateUser:', delegateUser);
    console.log('[SigningCeremony] activeAccountId:', activeAccountId);
    console.log('[SigningCeremony] account:', account);
    console.log('[SigningCeremony] group:', group);
    console.log('[SigningCeremony] defaultBrand:', defaultBrand);
    console.log('[SigningCeremony] recipient:', recipient);
    console.log('[SigningCeremony] transaction:', transaction);
    console.log('[SigningCeremony] documents:', documents);
    console.log('[SigningCeremony] approvals:', approvals);
    console.log('[SigningCeremony] fields:', fields);
    console.log('[SigningCeremony] attachments:', attachments);
    console.log('[SigningCeremony] configurations:', configurations);
    console.log('[SigningCeremony] documentVisibility:', documentVisibility);
    console.log('[SigningCeremony] allRecipients:', allRecipients);
    console.log('[SigningCeremony] verification:', verification);
    console.log('[SigningCeremony] accountProvider:', accountProvider);
    console.log('[SigningCeremony] signature:', signature);
    console.log('[SigningCeremony] accountFeatures:', accountFeatures);

    const enabledAccountFeatures = (Array.isArray(accountFeatures) ? accountFeatures : [])
        .filter(({is_active: isActive}) => isActive)
        .map(({key}) => key)
        .join(',');

    const {hasValidTransaction, hasValidRecipient} = useSignerData();
    const {isFaceToFaceHost} = useFaceToFaceRole();
    const {isSSORedirectInProgress} = useTwoFARedirect();
    const iframeViewportHeight = useIframeViewportHeight();
    const {isKeyboardVisible, viewportHeight, viewportOffsetTop} = useKeyboardVisibility();
    const futureSignerRedirectPath = useFutureSignerRedirectPath();

    const containerClassName = classNames('signing-ceremony', {
        'has-left-menu': hasLeftMenu,
        'menu-expanded': hasLeftMenu && isLeftMenuOpen,
        'is-keyboard-visible': isKeyboardVisible
    });

    const containerStyle = getCeremonyViewportStyle({
        iframeViewportHeight,
        isKeyboardVisible,
        viewportHeight,
        viewportOffsetTop
    });

    useInitializeSurvicateScript({
        // `signerUser` is the effective user (token `external_id`): on a delegation token it is the
        // impersonated account, while `delegateUser` holds the acting login — the latter identifies
        // signatures, not the survey respondent.
        userId: signerUser?.id || '',
        userEmail: signerUser?.email || recipient?.email || '',
        locale: locale || DEFAULT_LOCALE,
        createdDate: signerUser?.created_at || '',
        accountProperties: {
            // The account-service payload carries no country/company/plan; the user-service
            // record already fetched for `signerUser` has country and organisation (its name
            // for what App.jsx calls `company`), plus the same account id one hop earlier.
            country: signerUser?.attributes?.address?.country || '',
            company: signerUser?.organisation || '',
            accountId: signerUser?.session_account_id || account?.id || '',
            subscriptionPlan: ''
        },
        isSender: false,
        survicateWorkspaceKey: survicateWorkspaceKey || '',
        environment: config?.VS_ENV || '',
        theme: theme || '',
        userRole: parsedAccessToken?.role || '',
        features: enabledAccountFeatures
    });

    const isLoading = isInitialLoadInProgress || !hasValidTransaction || (!hasValidRecipient && !isFaceToFaceHost) || isSSORedirectInProgress;

    if (futureSignerRedirectPath) {
        return <Navigate to={futureSignerRedirectPath} replace />;
    }

    if (isLoading) {
        return <Loading isLoading />;
    }

    return (
        <div
            className={containerClassName}
            data-ceremony-role-email=""
            style={containerStyle}
        >
            <SignerUiIntlProvider>
                <DeclinedInterceptor />
                <HardwareTokenSigningContainer />
                <SignerUiHeader />
                {hasLeftMenu && <SignerUiMenu />}
                <ReassignPanel />
                <DeclinePanel />
                <Outlet />
            </SignerUiIntlProvider>
        </div>
    );
}
